import React, { useEffect, useState } from 'react'
import { COLORS } from '../theme'
import { TH, TD } from './tableStyles.jsx'

// The team profile: a lead's own people, twelve weeks, no rank.
//
// Every choice below follows from one fact measured on the day this was
// written: of the people at the bottom of the org's MR and story counts, most
// had left, were leaving, or worked where the crawl cannot see. So the page
// never sorts by a number, never shows a missing row as 0, says what each
// source cannot see, and gives the lead a place to write down what the numbers
// miss. See velocity-metrics ADR 0004.

const fmt = (v, digits = 0) => (v === null || v === undefined ? null : Number(v).toFixed(digits))

/** A value, or an em dash that says WHY there is none. Never a zero. */
function Cell({ value, digits = 0, why }) {
    const v = fmt(value, digits)
    return (
        <td style={{ ...TD, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
            {v === null ? <span title={why} style={{ color: COLORS.textMuted }}>—</span> : v}
        </td>
    )
}

const NO_JIRA = 'No resolved BP/SP/FP stories or bugs in the window — or their work is tracked somewhere else'
const NO_MRS = 'No merged MRs in the 3MP GitLab group — work in bstock-internal-tools repos is not crawled'

const COLS = [
    ['Stories', 'BP/SP/FP Story + Task resolved, by the assignee when the week was recorded'],
    ['Points', 'Story points on those items. Pods size differently — compare within a team'],
    ['Lead p50 (d)', 'Median days from created to resolved over their items'],
    ['Bugs resolved', 'BP/SP/FP bugs resolved with them as assignee'],
    ['MRs', 'Merged MRs they authored in the 3MP GitLab group'],
    ['Product-ticket', 'Of those MRs, how many name a product ticket (BP/SP/FP/BUGS/HP/GLOB). The rest are test, CI and chore work, or product work that names no ticket'],
    ['Open→merge p50 (h)', 'Median hours from MR opened to merged. Work before the MR opens is not in it'],
    ['AI-signed %', 'Share of their MRs with an AI co-author trailer. A floor — the trailer can be off'],
    ['Active wks', 'Weeks with at least one resolved story/bug or merged MR, whichever is more'],
]

function StatusBadge({ m }) {
    const bits = []
    if (m.status === 'exiting') bits.push(['Exiting', COLORS.warning])
    if (m.employment === 'contractor') bits.push([m.vendor ? `Contractor · ${m.vendor}` : 'Contractor', COLORS.textMuted])
    return bits.map(([label, color]) => (
        <span key={label} style={{ marginLeft: 8, fontSize: 11, color, border: `1px solid ${color}66`, borderRadius: 4, padding: '0 5px' }}>
            {label}
        </span>
    ))
}

function ContextNote({ member, onSaved }) {
    const [editing, setEditing] = useState(false)
    const [text, setText] = useState(member.context?.note || '')
    const [error, setError] = useState('')
    const save = async () => {
        setError('')
        const r = await fetch(`/api/hub/team/${member.id}/context`, {
            method: 'PATCH',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ note: text }),
        })
        const d = await r.json().catch(() => ({}))
        if (!r.ok) return setError(d.error || 'Could not save')
        setEditing(false)
        onSaved(d.context)
    }
    if (editing) {
        return (
            <div>
                <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    rows={3}
                    maxLength={2000}
                    placeholder="Leave, role, work the numbers can't see…"
                    style={{ width: '100%', minWidth: 220, background: COLORS.background, color: COLORS.text, border: `1px solid ${COLORS.border}`, borderRadius: 4, font: 'inherit', fontSize: 12, padding: 6 }}
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    <button onClick={save} style={btn(COLORS.primary)}>Save</button>
                    <button onClick={() => { setEditing(false); setText(member.context?.note || '') }} style={btn(COLORS.textMuted)}>Cancel</button>
                    {error && <span style={{ color: COLORS.danger, fontSize: 12 }}>{error}</span>}
                </div>
            </div>
        )
    }
    return (
        <button onClick={() => setEditing(true)} style={{ ...btn(COLORS.text), textAlign: 'left', whiteSpace: 'pre-wrap', maxWidth: 320 }}
            title={member.context ? `Last edited by ${member.context.by}${member.context.at ? ` on ${String(member.context.at).slice(0, 10)}` : ''}` : 'Add context'}>
            {member.context?.note || <span style={{ color: COLORS.textMuted }}>+ add context</span>}
        </button>
    )
}

const btn = (color) => ({ background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: 12, color, cursor: 'pointer' })

function AdminControls({ member, leads, onChanged }) {
    const patch = async (body) => {
        const r = await fetch(`/api/hub/team/${member.id}`, {
            method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
        })
        const d = await r.json().catch(() => ({}))
        if (!r.ok) return window.console.error(d.error)
        onChanged()
    }
    const sel = { background: COLORS.background, color: COLORS.textMuted, border: `1px solid ${COLORS.border}`, borderRadius: 4, fontSize: 11 }
    return (
        <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
            <select aria-label="Lead" value={leads.find((l) => l.name === member.lead)?.id ?? ''} onChange={(e) => patch({ leadId: e.target.value ? Number(e.target.value) : null })} style={sel}>
                <option value="">No lead</option>
                {leads.filter((l) => l.id !== member.id).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
            <select aria-label="Status" value={member.status} onChange={(e) => patch({ status: e.target.value })} style={sel}>
                <option value="active">active</option>
                <option value="exiting">exiting</option>
                <option value="exited">exited (hide)</option>
            </select>
        </div>
    )
}

export function TeamTable({ members, isAdmin, leads = [], onChanged = () => {}, onNote = () => {} }) {
    const groups = new Map()
    for (const m of members) {
        const k = m.lead || 'No lead assigned'
        if (!groups.has(k)) groups.set(k, [])
        groups.get(k).push(m)
    }
    return [...groups.entries()].map(([lead, people]) => (
        <section key={lead} style={{ marginBottom: 28 }}>
            <h2 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 8px', color: COLORS.text }}>
                {lead === 'No lead assigned' ? lead : `Reports to ${lead}`}
                <span style={{ color: COLORS.textMuted, fontWeight: 400 }}> · {people.length}</span>
            </h2>
            <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 980 }}>
                    <thead>
                        <tr>
                            <th style={TH}>Person</th>
                            {COLS.map(([label, title]) => (
                                <th key={label} title={title} style={{ ...TH, textAlign: 'right' }}>{label}</th>
                            ))}
                            <th style={{ ...TH, paddingLeft: 16 }}>Context (only this line of leads sees it)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {people.map((m) => (
                            <tr key={m.id}>
                                <td style={{ ...TD, whiteSpace: 'nowrap' }}>
                                    {m.name}
                                    <StatusBadge m={m} />
                                    {isAdmin && <AdminControls member={m} leads={leads} onChanged={onChanged} />}
                                </td>
                                <Cell value={m.work?.items} why={NO_JIRA} />
                                <Cell value={m.work?.points} why={NO_JIRA} />
                                <Cell value={m.work?.leadP50Days} digits={1} why={NO_JIRA} />
                                <Cell value={m.work?.bugsResolved} why={NO_JIRA} />
                                <Cell value={m.mrs?.mrs} why={NO_MRS} />
                                <Cell value={m.mrs?.jiraLinked} why={NO_MRS} />
                                <Cell value={m.mrs?.openToMergeP50Hours} digits={1} why={!m.mrs ? NO_MRS : m.gitlabAccounts > 1 ? 'Two GitLab accounts — a median cannot be combined' : 'No MR with a usable open and merge time'} />
                                <Cell value={m.mrs?.aiSignedShare} why={m.mrs ? 'Not measured for this window' : NO_MRS} />
                                <Cell value={m.work || m.mrs ? Math.max(m.work?.activeWeeks || 0, m.mrs?.activeWeeks || 0) : null} why="Nothing in either source" />
                                <td style={{ ...TD, paddingLeft: 16, verticalAlign: 'top' }}>
                                    <ContextNote member={m} onSaved={(c) => onNote(m.id, c)} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    ))
}

// The data's age and whether its feed is late, on the source's own weekly
// clock (CLAUDE.md, "The freshness contract"). Twelve weeks of numbers about a
// lead's own people, from a feed that died, must not look current.
const AGE = {
    live: { text: 'LIVE', color: COLORS.success },
    late: { text: 'FEED LATE', color: COLORS.warning },
    dark: { text: 'FEED DARK', color: COLORS.danger },
    unknown: { text: 'UNDATED', color: COLORS.danger },
}
export function ageText(seconds) {
    if (seconds == null) return 'age unknown'
    if (seconds < 3600) return `${Math.max(1, Math.round(seconds / 60))}m old`
    if (seconds < 172800) return `${Math.round(seconds / 3600)}h old`
    return `${Math.round(seconds / 86400)}d old`
}

export function SourceLine({ label, s }) {
    if (!s) return null
    if (s.state !== 'ok') {
        return <p style={{ color: COLORS.warning, fontSize: 12, margin: '2px 0' }}>{label}: {s.state} — {s.reason || 'no reason given'}</p>
    }
    const f = AGE[s.freshness?.state] || AGE.unknown
    return (
        <p style={{ color: COLORS.textMuted, fontSize: 12, margin: '2px 0' }}>
            <span style={{ color: f.color, fontWeight: 700, marginRight: 6 }}>{f.text}</span>
            <span style={{ marginRight: 6 }}>{ageText(s.freshness?.ageSeconds)}.</span>
            {s.label}. {s.note}
        </p>
    )
}

export default function Team() {
    const [data, setData] = useState(null)
    const [error, setError] = useState('')
    const load = () =>
        fetch('/api/hub/team', { credentials: 'include' })
            .then((r) => (r.ok ? r.json() : r.json().then((e) => Promise.reject(new Error(e.error)))))
            .then(setData)
            .catch((e) => setError(e.message || 'Team unavailable'))
    useEffect(() => { load() }, [])

    if (error) return <main style={{ padding: '32px' }}><p style={{ color: COLORS.warning }}>{error}</p></main>
    if (!data) return <main style={{ padding: '32px' }}><p style={{ color: COLORS.textMuted }}>Loading…</p></main>
    if (!data.viewer.hasReports) {
        return <main style={{ padding: '32px' }}><p style={{ color: COLORS.textMuted }}>Nobody reports to you on the roster. If that is wrong, ask an admin to set your people's lead.</p></main>
    }
    const onNote = (id, context) => setData((d) => ({ ...d, members: d.members.map((m) => (m.id === id ? { ...m, context } : m)) }))
    return (
        <main style={{ padding: '24px 32px 48px' }}>
            <p style={{ color: COLORS.text, fontSize: 13, maxWidth: 820, margin: '0 0 6px' }}>
                Context for a conversation with each person, not a score. Rows are alphabetical. A dash means the source has nothing for them — often because their work lives where the crawl cannot see, not because there was none.
            </p>
            <SourceLine label="Jira" s={data.sources?.work} />
            <SourceLine label="GitLab" s={data.sources?.mrs} />
            <div style={{ marginTop: 20 }}>
                <TeamTable members={data.members} isAdmin={data.viewer.isAdmin} leads={data.leads || []} onChanged={load} onNote={onNote} />
            </div>
        </main>
    )
}
